import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, RefreshCw, Trash2 } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import {
  OptOutKeywordModal,
  type OptOutKeywordFormValues,
} from '@/components/OptOutKeywordModal'
import { useAuthStore } from '@/stores/auth'
import type {
  OptOutKeyword,
  OptOutKeywordInvalidateResponse,
  OptOutKeywordListResponse,
  OptOutKeywordResponse,
  OptOutMatchType,
} from '@sdr-agend/admin-schemas'

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

const MATCH_TYPES: ReadonlyArray<OptOutMatchType | ''> = ['', 'literal', 'regex']
const ACTIVE_FILTERS: ReadonlyArray<{ label: string; value: '' | 'true' | 'false' }> = [
  { label: 'Todos', value: '' },
  { label: 'Ativos', value: 'true' },
  { label: 'Inativos', value: 'false' },
]

/**
 * `/admin/opt-out-keywords` — single-page CRUD for the opt-out keyword
 * registry. Story 3.9.
 *
 * UX decisions:
 *   - Compact table with inline `active` toggle (PATCH on click) so admins
 *     can flip suspect keywords without opening a detail page.
 *   - Creation goes through a modal — no separate route, per AC #5.
 *   - "Invalidar cache" surfaces the 3.4 hot-reload trigger; viewers don't
 *     see it. Hidden when the server reports `invalidated: false`? No — we
 *     keep the button visible and let admins discover that 3.4 isn't wired
 *     yet via the toast feedback.
 */
export function OptOutKeywordsPage(): React.ReactElement {
  const role = useAuthStore((s) => s.user?.role)
  const canMutate = role === 'admin' || role === 'manager'
  const isAdmin = role === 'admin'
  const queryClient = useQueryClient()

  const [matchTypeFilter, setMatchTypeFilter] = useState<OptOutMatchType | ''>('')
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('')
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })

  const queryKey = useMemo(
    () => ['admin-opt-out-keywords', { matchTypeFilter, activeFilter, search }],
    [matchTypeFilter, activeFilter, search],
  )

  const { data, isLoading, isError } = useQuery<OptOutKeywordListResponse>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams()
      if (matchTypeFilter !== '') params.set('matchType', matchTypeFilter)
      if (activeFilter !== '') params.set('active', activeFilter)
      if (search.trim() !== '') params.set('search', search.trim())
      const qs = params.toString()
      return api<OptOutKeywordListResponse>(
        `/admin/opt-out-keywords${qs === '' ? '' : `?${qs}`}`,
      )
    },
  })

  function notify(state: Omit<ToastState, 'open'>): void {
    setToast({ open: true, ...state })
  }

  const createMutation = useMutation({
    mutationFn: async (values: OptOutKeywordFormValues) =>
      api<OptOutKeywordResponse>('/admin/opt-out-keywords', {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: (res) => {
      notify({
        variant: 'default',
        title: 'Keyword criada',
        description: `"${res.keyword.keyword}" adicionada (${res.keyword.matchType}).`,
      })
      setModalOpen(false)
      setServerError(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-opt-out-keywords'] })
    },
    onError: (err: Error) => {
      const apiErr = err instanceof ApiError ? err : null
      const body = apiErr?.bodyJson as { error?: { message?: string } } | null
      setServerError(body?.error?.message ?? err.message)
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: async (kw: OptOutKeyword) =>
      api<OptOutKeywordResponse>(`/admin/opt-out-keywords/${kw.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !kw.active }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-opt-out-keywords'] })
    },
    onError: (err: Error) => {
      notify({ variant: 'destructive', title: 'Falha ao alternar status', description: err.message })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      api<OptOutKeywordResponse>(`/admin/opt-out-keywords/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Keyword removida' })
      void queryClient.invalidateQueries({ queryKey: ['admin-opt-out-keywords'] })
    },
    onError: (err: Error) => {
      notify({ variant: 'destructive', title: 'Falha ao remover', description: err.message })
    },
  })

  const invalidateMutation = useMutation({
    mutationFn: async () =>
      api<OptOutKeywordInvalidateResponse>('/admin/opt-out-keywords/invalidate-cache', {
        method: 'POST',
      }),
    onSuccess: (res) => {
      notify({
        variant: 'default',
        title: res.invalidated ? 'Cache invalidado' : 'Cache não disponível',
        description: res.invalidated
          ? 'Próximas mensagens recarregam keywords do banco.'
          : 'OptOutGate ainda não está integrado — alterações entram via TTL.',
      })
    },
    onError: (err: Error) => {
      notify({ variant: 'destructive', title: 'Falha ao invalidar cache', description: err.message })
    },
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Opt-out Keywords</h1>
          <p className="text-sm text-muted-foreground">
            Palavras e expressões que disparam opt-out automático em conversas.
          </p>
        </div>
        <div className="flex gap-2">
          {canMutate && (
            <Button
              type="button"
              variant="outline"
              onClick={() => invalidateMutation.mutate()}
              disabled={invalidateMutation.isPending}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {invalidateMutation.isPending ? 'Invalidando…' : 'Invalidar cache'}
            </Button>
          )}
          {canMutate && (
            <Button
              type="button"
              onClick={() => {
                setServerError(null)
                setModalOpen(true)
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nova Keyword
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select
          aria-label="Filtrar por tipo"
          value={matchTypeFilter}
          onChange={(e) => setMatchTypeFilter(e.target.value as OptOutMatchType | '')}
          className="max-w-[10rem]"
        >
          {MATCH_TYPES.map((t) => (
            <option key={t === '' ? 'all' : t} value={t}>
              {t === '' ? 'Todos os tipos' : t === 'literal' ? 'Literal' : 'Regex'}
            </option>
          ))}
        </Select>
        <div
          className="flex items-center gap-2"
          role="group"
          aria-label="Filtrar por status active"
        >
          {ACTIVE_FILTERS.map((opt) => {
            const active = activeFilter === opt.value
            return (
              <Button
                key={opt.label}
                type="button"
                variant={active ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter(opt.value)}
                aria-pressed={active}
              >
                {opt.label}
              </Button>
            )
          })}
        </div>
        <Input
          placeholder="Buscar por keyword…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Keyword</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Criada em</TableHead>
              {isAdmin && <TableHead className="w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-sm text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-sm text-destructive">
                  Erro ao carregar keywords.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && (data?.keywords ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-sm text-muted-foreground">
                  Nenhuma keyword encontrada.
                </TableCell>
              </TableRow>
            )}
            {(data?.keywords ?? []).map((kw: OptOutKeyword) => (
              <TableRow key={kw.id}>
                <TableCell className="font-mono text-sm">{kw.keyword}</TableCell>
                <TableCell>
                  <Badge variant={kw.matchType === 'regex' ? 'secondary' : 'outline'}>
                    {kw.matchType === 'regex' ? 'Regex' : 'Literal'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={kw.active}
                      disabled={!canMutate || toggleActiveMutation.isPending}
                      onChange={() => toggleActiveMutation.mutate(kw)}
                      aria-label={`Alternar status active de ${kw.keyword}`}
                      className="h-4 w-4"
                    />
                    <span className={kw.active ? 'text-foreground' : 'text-muted-foreground'}>
                      {kw.active ? 'Sim' : 'Não'}
                    </span>
                  </label>
                </TableCell>
                <TableCell>{new Date(kw.createdAt).toLocaleString('pt-BR')}</TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm(`Remover keyword "${kw.keyword}"?`)) {
                          deleteMutation.mutate(kw.id)
                        }
                      }}
                      aria-label={`Remover ${kw.keyword}`}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <OptOutKeywordModal
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open)
          if (!open) setServerError(null)
        }}
        onSubmit={(values) => createMutation.mutate(values)}
        submitting={createMutation.isPending}
        serverError={serverError}
      />

      <Toast
        open={toast.open}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>
    </div>
  )
}

export default OptOutKeywordsPage
